import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

import { useParking } from 'src/providers/ParkingProvider';
import { ApiKey } from 'src/features/apiKeys/utils/types';
import { useDeleteApiKey } from 'src/features/apiKeys/api/useDeleteApiKey';
import { LIST_API_KEYS_RESOURCE } from 'src/features/apiKeys/api/useListApiKeys';
import { apiKeysMessages } from 'src/features/apiKeys/utils/apiKeysMessages';

interface DeleteApiKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedApiKey: ApiKey;
    children?: React.ReactNode;
}

function DeleteApiKeyFormModal<TData>({ selectedApiKey, children }: DeleteApiKeyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const dialogTitle = `${formatMessage(generalMessages.delete)} ${formatMessage(apiKeysMessages.apiKey)}`;

    const { mutate: deleteApiKey, isPending: isPendingDelete } = useDeleteApiKey({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_API_KEYS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(apiKeysMessages.deleteApiKeySuccess),
                description: formatMessage(apiKeysMessages.deleteApiKeySuccessDescription, {
                    entry: selectedApiKey.name,
                }),
            });
        },
    });

    const onSubmit = () => deleteApiKey({ keyId: selectedApiKey.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>{dialogTitle}</DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={apiKeysMessages.deleteApiKeySubtitle.id}
                                    defaultMessage={apiKeysMessages.deleteApiKeySubtitle.defaultMessage}
                                    values={{
                                        entry: selectedApiKey.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={apiKeysMessages.confirmDeleteApiKey.id}
                            defaultMessage={apiKeysMessages.confirmDeleteApiKey.defaultMessage}
                            values={{
                                entry: selectedApiKey.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        {dialogTitle}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteApiKeyFormModal;
